import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Chip,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  GetApp as InstallIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  isIOS,
  isPWAInstalled,
  getPWAInstallInstructions,
  areNotificationsSupported,
  getBrowserLimitations,
} from "../../utils/browserDetection";
import { notificationService } from "../../services/notificationService";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  initializeNotifications,
  saveNotificationPreferences,
} from "../../features/notifications/notificationSlice";
import { firebaseMessagingConfig } from "../../config/firebase-messaging";
import IOSInstallInstructions from "./IOSInstallInstructions";

interface PushNotificationBlockProps {
  userId: string;
}

const PushNotificationBlock: React.FC<PushNotificationBlockProps> = ({
  userId,
}) => {
  const dispatch = useAppDispatch();
  const { preferences, loading } = useAppSelector(
    (state) => state.notifications
  );
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPWA = isPWAInstalled();
  const ios = isIOS();
  const pushSupported = areNotificationsSupported();
  const browserLimitations = getBrowserLimitations();
  const instructions = getPWAInstallInstructions();

  // Déterminer si les notifications push sont réellement fonctionnelles
  const isPushActuallyWorking = () => {
    if (!pushEnabled) return false;
    if (!pushSupported) return false;
    if (ios && !isPWA) return false; // Sur iOS, seulement si PWA installée
    return true;
  };

  // Déterminer l'état par défaut des notifications push
  const getDefaultPushState = () => {
    // Les notifications push sont désactivées par défaut sur toutes les plateformes
    // L'utilisateur doit les activer explicitement
    return false;
  };

  const [pushEnabled, setPushEnabled] = useState(getDefaultPushState());

  // Initialiser l'état depuis les préférences
  useEffect(() => {
    console.log(
      "🔔 [DEBUG] PushNotificationBlock - Mise à jour des préférences:",
      preferences
    );
    console.log(
      "🔔 [DEBUG] PushNotificationBlock - fcmToken dans les préférences:",
      !!preferences?.fcmToken
    );

    if (preferences?.fcmToken) {
      console.log(
        "🔔 [DEBUG] PushNotificationBlock - Token FCM trouvé, activation des notifications push"
      );
      setPushEnabled(true);
    } else {
      console.log(
        "🔔 [DEBUG] PushNotificationBlock - Aucun token FCM, état par défaut:",
        getDefaultPushState()
      );
      setPushEnabled(getDefaultPushState());
    }
  }, [preferences]);

  const handlePushToggle = async () => {
    if (!pushEnabled) {
      // Activer les notifications push
      try {
        setError(null);

        if (!firebaseMessagingConfig.vapidKey) {
          setError(
            "Configuration Firebase manquante. Contactez l'administrateur."
          );
          return;
        }

        const token = await notificationService.requestPermissionAndInitialize(
          userId
        );

        if (token) {
          await dispatch(
            saveNotificationPreferences({
              userId,
              preferences: {
                fcmToken: token,
              },
            })
          ).unwrap();
          setPushEnabled(true);
        } else {
          setError(
            "Impossible d'obtenir la permission pour les notifications push."
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de l'activation des notifications push:",
          error
        );
        setError("Erreur lors de l'activation des notifications push.");
      }
    } else {
      // Désactiver les notifications push
      try {
        setError(null);

        // Supprimer le token FCM de la base de données
        await notificationService.removeFCMToken(userId);

        // Mettre à jour les préférences sans token FCM
        // On ne spécifie pas fcmToken du tout pour qu'il soit supprimé
        await dispatch(
          saveNotificationPreferences({
            userId,
            preferences: {
              // fcmToken sera supprimé automatiquement par le service
            },
          })
        ).unwrap();

        setPushEnabled(false);
      } catch (error) {
        console.error(
          "Erreur lors de la désactivation des notifications push:",
          error
        );
        setError("Erreur lors de la désactivation des notifications push.");
      }
    }
  };

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  const handleCloseIOSInstructions = () => {
    console.log("🔧 [DEBUG] Fermeture des instructions iOS");
    setShowIOSInstructions(false);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <NotificationsIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" component="h3">
            Notifications Push
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {isPushActuallyWorking() && (
              <Chip label="Activé" color="success" size="small" />
            )}
            {pushEnabled && ios && !isPWA && (
              <Chip label="Token sauvegardé" color="warning" size="small" />
            )}
            {!pushSupported && (
              <Chip label="Non supporté" color="error" size="small" />
            )}
            {ios && !isPWA && <Chip label="iOS" color="warning" size="small" />}
            {isPWA && <Chip label="PWA" color="info" size="small" />}
          </Box>
        </Box>

        {/* État actuel */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={pushEnabled}
                onChange={handlePushToggle}
                disabled={!pushSupported || loading}
                color="primary"
              />
            }
            label={`Notifications push ${
              pushEnabled ? "activées" : "désactivées"
            }`}
          />
        </Box>

        {/* Messages d'information selon le contexte */}
        {!pushSupported && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Les notifications push ne sont pas supportées sur votre navigateur.
            <br />
            Limitations détectées : {browserLimitations.join(", ")}
          </Alert>
        )}

        {ios && !isPWA && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sur iOS, les notifications push ne fonctionnent que dans
            l'application installée.
            {pushEnabled && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                Note : Un token de notification existe mais ne fonctionnera
                qu'après installation de l'application.
              </Typography>
            )}
            <br />
            <Button
              variant="outlined"
              size="small"
              startIcon={<InstallIcon />}
              onClick={handleShowIOSInstructions}
              sx={{ mt: 1 }}
            >
              Instructions d'installation
            </Button>
          </Alert>
        )}

        {isPWA && pushEnabled && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Application installée et notifications push activées.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Informations générales */}
        <Typography variant="body2" color="text.secondary">
          Les notifications push vous alertent en temps réel des changements de
          statut des matchs de vos joueurs suivis.
          {!pushEnabled && pushSupported && (
            <span style={{ fontWeight: "bold", color: "primary.main" }}>
              {" "}
              Activez les notifications push pour recevoir ces alertes.
            </span>
          )}
        </Typography>

        {/* Instructions d'installation iOS */}
        {showIOSInstructions && (
          <Box sx={{ mt: 2 }}>
            <IOSInstallInstructions onClose={handleCloseIOSInstructions} />
          </Box>
        )}
      </Paper>
    </>
  );
};

export default PushNotificationBlock;
